import { CollectionResource } from 'dy-frontend-http-repository/lib/data/resources';
import {
    DesignerPickerItemResource,
    TaskSubTicketDetailResource,
    TaskSubTicketResource,
    TaskSubTicketStatusLogResource,
    TaskSubTicketSummaryResource,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';
import { TimeTrackingSessionResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';

import { INITIAL_SUB_TICKET_READ, SubTicketReadState } from '../utils/subTicketListRead';

import domain from './domain';
import {
    SubTicketDueDateRange,
    resetSubTickets,
    resetSubTicketStatusLog,
    resetSubTicketSummary,
} from './events';

// The sub-ticket list, inseparable from the Request it belongs to and from whether that Request
// has actually answered.
//
// One store rather than three, because the three facts are only meaningful together. Rows
// without their owning task can be rendered under a different Request; a status without its task
// can report a previous Request's settlement as this one's; and a null collection on its own
// conflates "still loading", "loaded and empty" and "the read failed". Pairing them means the
// only way to read rows is to say which Request you are reading them for.
export interface SubTicketListState extends SubTicketReadState {
    collection: CollectionResource<TaskSubTicketResource, {}> | null;
}

export const INITIAL_SUB_TICKET_LIST: SubTicketListState = { ...INITIAL_SUB_TICKET_READ, collection: null };

export const $subTicketList = domain.createStore<SubTicketListState>(INITIAL_SUB_TICKET_LIST).reset(resetSubTickets);

// Read-state view, for consumers that only ask "has this Request answered yet?"
export const $subTicketListRead = $subTicketList.map(
    ({ status, taskId }): SubTicketReadState => ({ status, taskId })
);

// Rows, and ONLY when the store is holding them for the Request asked about. Everything that
// renders or reasons over rows goes through this rather than reaching into the collection.
export const selectSubTicketRows = (
    list: SubTicketListState,
    taskId: ID
): TaskSubTicketResource[] | null => (list.taskId === taskId ? list.collection?.items ?? null : null);

// Aside summary counters. Reset only by the aside, never by page teardown.
export const $subTicketSummary = domain
    .createStore<TaskSubTicketSummaryResource | null>(null)
    .reset(resetSubTicketSummary);

// The parent Request's sub-task status transitions, paired with the Request they were read for.
//
// Paired for the same reason the list is: the state log outlives a tab switch, so a reply that
// arrives after the user has moved to another Request must be droppable on sight rather than
// merged into whatever timeline is on screen now.
export interface SubTicketStatusLogState {
    taskId: ID | null;
    items: TaskSubTicketStatusLogResource[];
}

export const INITIAL_SUB_TICKET_STATUS_LOG: SubTicketStatusLogState = { taskId: null, items: [] };

export const $subTicketStatusLog = domain
    .createStore<SubTicketStatusLogState>(INITIAL_SUB_TICKET_STATUS_LOG)
    .reset(resetSubTicketStatusLog);

// Rows, and ONLY when they belong to the Request asked about
export const selectSubTicketStatusLogRows = (
    state: SubTicketStatusLogState,
    taskId: ID
): TaskSubTicketStatusLogResource[] => (state.taskId === taskId ? state.items : []);

export type SubTicketDetailStatus = 'loading' | 'loaded' | 'error';

// Per sub-ticket, so a failure on one never leaves another panel spinning
export interface SubTicketDetailState {
    status: SubTicketDetailStatus;
    detail: TaskSubTicketDetailResource | null;
}

export const $subTicketDetailStates = domain
    .createStore<{ [subTicketId: string]: SubTicketDetailState }>({})
    .reset(resetSubTickets);

// The caller's single open timer session — the authoritative answer to "is a timer running,
// and where". List rows carry their own `active_session`, but that is a snapshot from whenever
// the list was last read; anything load-bearing derives from this store instead.
export const $activeTimerSession = domain
    .createStore<TimeTrackingSessionResource | null>(null)
    .reset(resetSubTickets);

// Restoration in flight. `null` session alone is ambiguous — it is both "not finished" and
// "no open session" — and starting on the first reading would double-start on the second.
export const $isActiveTimerSessionLoading = domain.createStore<boolean>(true).reset(resetSubTickets);

// A failed read is NOT a confirmed absence of a session. Kept apart so the UI can say
// "could not check" instead of silently offering Start over a session that may exist.
export const $activeTimerSessionError = domain.createStore<boolean>(false).reset(resetSubTickets);

// Designers assignable to a sub-ticket of this Request, with their participation flag.
//
// Paired with the Request that produced them for the same reason the list is: the picker names
// people who are assignable to ONE Request and carries their participation in it. Offered under
// a different Request, a stale entry would seed a modal with someone who may not be assignable
// there at all, and its `is_participant` flag would describe the wrong Request.
export interface DesignerPickerState extends SubTicketReadState {
    items: DesignerPickerItemResource[] | null;
}

export const INITIAL_DESIGNER_PICKER: DesignerPickerState = { ...INITIAL_SUB_TICKET_READ, items: null };

export const $designerPicker = domain.createStore<DesignerPickerState>(INITIAL_DESIGNER_PICKER).reset(resetSubTickets);

// Archived sub-tickets are hidden unless explicitly requested
export const $isArchivedShown = domain.createStore<boolean>(false).reset(resetSubTickets);

// "Only mine" narrows the list to the caller's own phases
export const $isOnlyMineShown = domain.createStore<boolean>(false).reset(resetSubTickets);

// Status filter chips; empty means no filter
export const $statusFilter = domain.createStore<TaskSubTicketStatus[]>([]).reset(resetSubTickets);

// Assigned-designer filter; empty means no filter.
//
// Ids rather than the designers themselves: the options are re-derived from the loaded rows on
// every read, so holding objects would leave the selection pointing at instances a refresh has
// already replaced. Reset with the page, exactly like every other filter — a selection made
// against one Request's designers means nothing under the next one.
export const $designerFilter = domain.createStore<ID[]>([]).reset(resetSubTickets);

// Applied due-date range in calendar days; both null means no filter
export const INITIAL_DUE_DATE_RANGE: SubTicketDueDateRange = { fromDate: null, toDate: null };

export const $dueDateRange = domain
    .createStore<SubTicketDueDateRange>(INITIAL_DUE_DATE_RANGE)
    .reset(resetSubTickets);

// Expanded row, or null
export const $expandedSubTicketId = domain.createStore<ID | null>(null).reset(resetSubTickets);
